import { directories } from "./directories.service";
import { editorialCategories } from "./editorialCategories.service";
import { researchCms, type EditionBpa, type EditionConfig } from "./researchCms.service";

type ResearchRow = { id: string };

type ResearchPageReader<T> = (limit: number, offset: number) => Promise<T[]>;

type ResearchReadLimits<T> = {
  pageSize: number;
  maxRows: number;
  resource: string;
  merge: (kept: T, duplicate: T) => T;
};

function keepFirst<T>(kept: T): T {
  return kept;
}

function mergeEditionConfigs(kept: EditionConfig, duplicate: EditionConfig): EditionConfig {
  if (duplicate.wpId === null || duplicate.wpId === kept.wpId) return kept;
  if (kept.wpId !== null) {
    throw new Error(`Edition ${kept.editionId} has conflicting legacy ids`);
  }
  return { ...kept, wpId: duplicate.wpId };
}

const EDITION_CONFIG_READ_LIMITS: ResearchReadLimits<EditionConfig> = {
  pageSize: 100,
  maxRows: 1000,
  resource: "edition configs",
  merge: mergeEditionConfigs,
};

const EDITION_BPA_READ_LIMITS: ResearchReadLimits<EditionBpa> = {
  pageSize: 200,
  maxRows: 2000,
  resource: "edition practice areas",
  merge: keepFirst,
};

async function readAllResearchPages<T extends ResearchRow>(
  readPage: ResearchPageReader<T>,
  { pageSize, maxRows, resource, merge }: ResearchReadLimits<T>,
): Promise<T[]> {
  const byId = new Map<string, T>();
  let offset = 0;
  for (;;) {
    const rows = await readPage(pageSize, offset);
    if (rows.length > 0 && offset >= maxRows) {
      throw new Error(`Could not read all ${resource} from research-api (stopped at ${offset})`);
    }
    offset += rows.length;
    for (const row of rows) {
      const kept = byId.get(row.id);
      byId.set(row.id, kept ? merge(kept, row) : row);
    }
    if (rows.length < pageSize) return [...byId.values()];
  }
}

function readAllEditionConfigs(): Promise<EditionConfig[]> {
  return readAllResearchPages(async (limit, offset) => {
    const response = await researchCms.getEditions({ limit, offset });
    return response.editionConfigs;
  }, EDITION_CONFIG_READ_LIMITS);
}

function readAllEditionBpas(editionId: string): Promise<EditionBpa[]> {
  return readAllResearchPages(async (limit, offset) => {
    const response = await researchCms.getEditionBpas({ editionId, limit, offset });
    return response.editionBpas;
  }, EDITION_BPA_READ_LIMITS);
}

export type NewAxisEdition = {
  editionId: string;
  publicationId: number | null;
  publication: string;
  editionName: string;
  label: string;
};

export type DisplayRuleOption = { value: string; label: string };

export type DisplayRulePracticeAreaOption = DisplayRuleOption & {
  region: string | null;
  baseName: string;
};

export type DisplayRuleOptions = {
  region: DisplayRuleOption[];
  practiceGroup: DisplayRuleOption[];
  practiceArea: DisplayRulePracticeAreaOption[];
};

export const EMPTY_DISPLAY_RULE_OPTIONS: DisplayRuleOptions = {
  region: [],
  practiceGroup: [],
  practiceArea: [],
};

export function newAxisEditionLabel(publication: string, editionName: string): string {
  return editionName ? `${publication} — ${editionName}` : publication;
}

export function newAxisEditionOptions(
  editions: NewAxisEdition[] | null | undefined,
  year?: string,
): DisplayRuleOption[] {
  return (editions ?? [])
    .filter(edition => !year || edition.editionName.includes(year))
    .map(edition => ({ value: edition.editionId, label: edition.label }));
}

function uniqueEditionConfigs(configs: EditionConfig[]): EditionConfig[] {
  const byEditionId = new Map<string, EditionConfig>();
  for (const config of configs) {
    const kept = byEditionId.get(config.editionId);
    byEditionId.set(config.editionId, kept ? mergeEditionConfigs(kept, config) : config);
  }
  return [...byEditionId.values()];
}

function toNewAxisEdition(
  config: EditionConfig,
  directoryNames: Map<string, string>,
): NewAxisEdition | null {
  const publication = config.directoryId ? directoryNames.get(config.directoryId) : undefined;
  if (!publication) return null;
  const editionName = config.editionName ?? "";
  return {
    editionId: config.editionId,
    publicationId: config.wpId,
    publication,
    editionName,
    label: newAxisEditionLabel(publication, editionName),
  };
}

export async function listNewAxisEditions(): Promise<NewAxisEdition[]> {
  const [configs, directoryList] = await Promise.all([
    readAllEditionConfigs(),
    directories.getAll(),
  ]);
  const directoryNames = new Map(directoryList.map(directory => [directory.id, directory.name]));
  return uniqueEditionConfigs(configs)
    .map(config => toNewAxisEdition(config, directoryNames))
    .filter((edition): edition is NewAxisEdition => edition !== null)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export async function getNewAxisEdition(editionId: string): Promise<NewAxisEdition> {
  const response = await researchCms.getEditions({ editionId });
  const [config] = uniqueEditionConfigs(response.editionConfigs);
  if (!config) throw new Error(`Edition ${editionId} not found`);
  if (!config.directoryId) throw new Error(`Edition ${editionId} has no publication`);
  const directory = await directories.getById(config.directoryId);
  const edition = toNewAxisEdition(config, new Map([[directory.id, directory.name]]));
  if (!edition) throw new Error(`Edition ${editionId} has no publication`);
  return edition;
}

function byLabel(a: DisplayRuleOption, b: DisplayRuleOption): number {
  return a.label.localeCompare(b.label);
}

function joinLabel(parts: Array<string | null | undefined>): string {
  return parts.filter((part): part is string => Boolean(part)).join(" > ");
}

export async function loadLegacyDisplayRuleOptions(
  publicationId: number,
): Promise<DisplayRuleOptions> {
  const [regionsResponse, submissionBpasResponse] = await Promise.all([
    editorialCategories.getAll({ publicationId, type: "country", limit: 100 }),
    editorialCategories.getSubmissionBpas(publicationId),
  ]);

  const regions = new Map<string, string>();
  for (const category of regionsResponse.editorialCategories ?? []) {
    const value = category.countryId ? String(category.countryId) : String(category.id);
    regions.set(value, category.country || category.subtype || `Region ${value}`);
  }

  const practiceAreas = submissionBpasResponse.practiceAreas.map(area => {
    const group =
      area.practiceGroupName && area.practiceGroupName !== area.name
        ? area.practiceGroupName
        : null;
    return {
      value: String(area.id),
      label: joinLabel([area.countryName, group, area.name]),
      region: area.countryId != null ? String(area.countryId) : null,
      baseName: joinLabel([group, area.name]),
    };
  });

  return {
    region: [...regions.entries()].map(([value, label]) => ({ value, label })).sort(byLabel),
    practiceGroup: submissionBpasResponse.practiceGroups
      .map(group => ({ value: String(group.id), label: group.name }))
      .sort(byLabel),
    practiceArea: practiceAreas.sort(byLabel),
  };
}

export async function loadNewAxisDisplayRuleOptions(
  editionId: string,
): Promise<DisplayRuleOptions> {
  const editionBpas = await readAllEditionBpas(editionId);

  const regions = new Map<string, string>();
  const practiceGroups = new Map<string, string>();
  const practiceAreas = new Map<string, DisplayRulePracticeAreaOption>();
  for (const editionBpa of editionBpas) {
    regions.set(editionBpa.rankingEntityId, editionBpa.rankingEntityName);
    if (editionBpa.practiceGroupId && editionBpa.practiceGroupName) {
      practiceGroups.set(editionBpa.practiceGroupId, editionBpa.practiceGroupName);
    }
    const group =
      editionBpa.practiceGroupName && editionBpa.practiceGroupName !== editionBpa.bpaName
        ? editionBpa.practiceGroupName
        : null;
    practiceAreas.set(editionBpa.bpaId, {
      value: editionBpa.bpaId,
      label: joinLabel([editionBpa.rankingEntityName, group, editionBpa.bpaName]),
      region: editionBpa.rankingEntityId,
      baseName: joinLabel([group, editionBpa.bpaName]),
    });
  }

  return {
    region: [...regions.entries()].map(([value, label]) => ({ value, label })).sort(byLabel),
    practiceGroup: [...practiceGroups.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort(byLabel),
    practiceArea: [...practiceAreas.values()].sort(byLabel),
  };
}
